import { Accordion, AccordionDetails, AccordionSummary, Button, TextField } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  fetchSofiaDomainAppConfiguration,
  updateSofiaDomainAppConfiguration,
} from '../../repository/jago/domainsAPI';
import { IAppConfigurationOutDTO } from '../../repository/jago/model/input/IAppConfigurationDTO';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { Roles } from '../../services/PermissionService/PermissionConstants';
import Permission from '../Permission/Permission';
import ReduxLanguage from '../ReduxLanguage';
import {
  formatMultilineList,
  parseMultilineList,
  validateAppConfiguration,
} from '../../utils/domainAppConfigurationUtils';

const styles = createStyles({
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
  },
  sectionTitle: {
    fontWeight: 'bold',
  },
  sectionSubtitle: {
    fontWeight: 100,
    lineHeight: '1.5em',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  statusText: {
    color: '#607D8B',
  },
  saveError: {
    color: 'red',
    marginTop: 12,
  },
  field: {
    marginTop: 16,
  },
  hint: {
    marginTop: 8,
    fontSize: 13,
    color: '#607D8B',
    fontStyle: 'italic',
  },
});

type IDomainAppConfigurationViewReduxProps = ConnectedProps<typeof connector>;

export interface IDomainAppConfigurationViewProps extends WithStyles<typeof styles>, IDomainAppConfigurationViewReduxProps {
  domainHostname: string;
}

interface IDomainAppConfigurationViewState {
  expanded: boolean;
  loading: boolean;
  loaded: boolean;
  error: boolean;
  saving: boolean;
  saveError: boolean;
  validationErrorKey: string | null;
  customLoginCheckUrlsText: string;
  customLoginCheckFieldsText: string;
}

class DomainAppConfigurationView extends React.Component<IDomainAppConfigurationViewProps, IDomainAppConfigurationViewState> {
  constructor(props: IDomainAppConfigurationViewProps) {
    super(props);
    this.state = {
      expanded: false,
      loading: false,
      loaded: false,
      error: false,
      saving: false,
      saveError: false,
      validationErrorKey: null,
      customLoginCheckUrlsText: '',
      customLoginCheckFieldsText: '',
    };
  }

  public componentDidUpdate(prevProps: IDomainAppConfigurationViewProps) {
    if (prevProps.domainHostname !== this.props.domainHostname) {
      this.setState({
        expanded: false,
        loading: false,
        loaded: false,
        error: false,
        saving: false,
        saveError: false,
        validationErrorKey: null,
        customLoginCheckUrlsText: '',
        customLoginCheckFieldsText: '',
      });
    }
  }

  private applyConfiguration(config: IAppConfigurationOutDTO) {
    this.setState({
      customLoginCheckUrlsText: formatMultilineList(config.customLoginCheckUrls),
      customLoginCheckFieldsText: formatMultilineList(config.customLoginCheckFields),
    });
  }

  public async onToggle(_event: React.ChangeEvent<{}>, expanded: boolean) {
    const { domainHostname } = this.props;
    const { loaded, loading } = this.state;
    this.setState({ expanded });
    if (!expanded || loaded || loading || !domainHostname) {
      return;
    }
    this.setState({ loading: true, error: false });
    try {
      const response = await fetchSofiaDomainAppConfiguration(domainHostname);
      this.applyConfiguration(response.data || {});
      this.setState({ loaded: true, loading: false });
    } catch (loadError) {
      this.setState({ error: true, loaded: true, loading: false });
    }
  }

  public async onSave() {
    const { domainHostname } = this.props;
    const { saving, customLoginCheckUrlsText, customLoginCheckFieldsText } = this.state;
    if (!domainHostname || saving) {
      return;
    }
    const customLoginCheckUrls = parseMultilineList(customLoginCheckUrlsText);
    const customLoginCheckFields = parseMultilineList(customLoginCheckFieldsText);
    const validationErrorKey = validateAppConfiguration(customLoginCheckUrls, customLoginCheckFields);
    if (validationErrorKey) {
      this.setState({ validationErrorKey, saveError: false });
      return;
    }
    this.setState({ saving: true, saveError: false, validationErrorKey: null });
    try {
      const response = await updateSofiaDomainAppConfiguration(domainHostname, {
        customLoginCheckUrls,
        customLoginCheckFields,
      });
      this.applyConfiguration(response.data || {});
      this.setState({ saving: false });
    } catch (saveError) {
      this.setState({ saving: false, saveError: true });
    }
  }

  public render() {
    const { classes, domainHostname } = this.props;
    const {
      expanded,
      loading,
      error,
      saving,
      saveError,
      validationErrorKey,
      customLoginCheckUrlsText,
      customLoginCheckFieldsText,
    } = this.state;
    if (!domainHostname) {
      return null;
    }
    return (
      <Permission abilityHelper={AbilityProvider.getAbilityHelper()} roles={[Roles.ROLE_VASHO_SOFIA_ADMIN]}>
        <Accordion style={{ marginTop: 20 }} expanded={expanded} onChange={(event, isExpanded) => this.onToggle(event, isExpanded)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className={classes.sectionHeader}>
              <h3 className={classes.sectionTitle}><ReduxLanguage languageKey="domains.domainAppConfigurationTitle" /></h3>
              <h4 className={classes.sectionSubtitle}><ReduxLanguage languageKey="domains.domainAppConfigurationDescription" /></h4>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div className={classes.body}>
              {loading ? <h4 className={classes.statusText}><ReduxLanguage languageKey="forms.spinnerLoading" /></h4> : null}
              {error ? <h4 className={classes.statusText}><ReduxLanguage languageKey="domains.domainAppConfigurationLoadError" /></h4> : null}
              {!loading && !error ? (
                <>
                  <p className={classes.hint}><ReduxLanguage languageKey="domains.domainAppConfigurationUrlsHint" /></p>
                  <TextField
                    className={classes.field}
                    label={<ReduxLanguage languageKey="domains.domainAppConfigurationUrlsLabel" />}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={4}
                    disabled={saving}
                    value={customLoginCheckUrlsText}
                    onChange={(event) => this.setState({
                      customLoginCheckUrlsText: event.target.value,
                      validationErrorKey: null,
                      saveError: false,
                    })}
                  />
                  <p className={classes.hint}><ReduxLanguage languageKey="domains.domainAppConfigurationFieldsHint" /></p>
                  <TextField
                    className={classes.field}
                    label={<ReduxLanguage languageKey="domains.domainAppConfigurationFieldsLabel" />}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    disabled={saving}
                    value={customLoginCheckFieldsText}
                    onChange={(event) => this.setState({
                      customLoginCheckFieldsText: event.target.value,
                      validationErrorKey: null,
                      saveError: false,
                    })}
                  />
                  <Button
                    variant="contained"
                    disabled={saving}
                    style={{
                      width: '100%',
                      marginTop: 20,
                      backgroundColor: '#5AC0B1',
                      color: 'white',
                      fontWeight: 'bold',
                      opacity: saving ? 0.6 : 1,
                    }}
                    onClick={() => this.onSave()}
                  >
                    <ReduxLanguage languageKey="forms.save" />
                  </Button>
                </>
              ) : null}
              {validationErrorKey ? <h4 className={classes.saveError}><ReduxLanguage languageKey={validationErrorKey} /></h4> : null}
              {saveError ? <h4 className={classes.saveError}><ReduxLanguage languageKey="domains.domainAppConfigurationSaveError" /></h4> : null}
            </div>
          </AccordionDetails>
        </Accordion>
      </Permission>
    );
  }
}

const connector = connect();
export default connector(withStyles(styles)(DomainAppConfigurationView));
